import { Link, useParams, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Navbar } from "@/components/Navbar";
import { SiteFooter } from "@/components/SiteFooter";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { CheckCircle2, ArrowRight, Phone } from "lucide-react";
import { useSiteSettings } from "@/lib/settings";
import { findService, SERVICES } from "@/data/services";
import { TEXT_GUIDES } from "@/data/textGuides";
import { PHONE_DISPLAY, PHONE_HREF } from "@/lib/contact";

export default function ServicePage({ slug: slugProp }) {
  const params = useParams();
  const service = findService(slugProp || params.slug);
  const settings = useSiteSettings();

  if (!service) return <Navigate to="/pricing" replace />;

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        name: service.title,
        serviceType: service.title,
        description: service.metaDescription,
        provider: { "@type": "EducationalOrganization", name: "Tutors Melbourne", telephone: PHONE_DISPLAY },
        areaServed: { "@type": "City", name: "Melbourne" },
        audience: { "@type": "EducationalAudience", educationalRole: "student" },
      },
      {
        "@type": "FAQPage",
        mainEntity: service.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#0f1117]" data-testid={`service-page-${service.slug}`}>
      <Seo title={service.metaTitle} rawTitle description={service.metaDescription} path={`/${service.slug}`} jsonLd={jsonLd} />
      <Navbar />

      <section className="pt-28 pb-12 px-6 bg-gradient-to-b from-brand-purple-soft/40 to-white dark:from-brand-purple/10 dark:to-[#0f1117]">
        <Reveal className="max-w-3xl mx-auto">
          <Badge className="bg-brand-gold text-white mb-4 font-body font-semibold px-4 py-1.5 rounded-full">
            Enrolling for {settings.term_label}
          </Badge>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 dark:text-white leading-tight mb-5">
            {service.h1}
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-300 font-body leading-relaxed">{service.intro}</p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/enrol">
              <Button className="rounded-full bg-brand-purple hover:bg-brand-purple-hover text-white font-body font-semibold px-7 py-6" data-testid="service-enrol-cta">
                Enrol For {settings.term_label} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <a href={PHONE_HREF}>
              <Button variant="outline" className="rounded-full border-brand-purple text-brand-purple hover:bg-brand-purple-soft font-body font-semibold px-7 py-6" data-testid="service-call-cta">
                <Phone className="mr-2 h-4 w-4" /> {PHONE_DISPLAY}
              </Button>
            </a>
          </div>
        </Reveal>
      </section>

      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto space-y-14">
          {service.sections.map((s) => (
            <Reveal key={s.h2}>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 dark:text-white mb-4">{s.h2}</h2>
              <p className="text-base text-slate-600 dark:text-slate-300 font-body leading-relaxed">{s.body}</p>
              {s.list && (
                <ul className="mt-5 space-y-3">
                  {s.list.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-slate-700 dark:text-slate-200 font-body">
                      <CheckCircle2 className="h-5 w-5 text-brand-gold shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Reveal>
          ))}

          <Reveal>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 dark:text-white mb-6">Common questions</h2>
            <div className="space-y-5">
              {service.faqs.map((f) => (
                <div key={f.q} className="border-l-4 border-brand-purple/30 pl-5">
                  <h3 className="font-heading text-lg font-semibold text-slate-900 dark:text-white mb-1.5">{f.q}</h3>
                  <p className="text-sm text-slate-600 dark:text-slate-300 font-body leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal className="rounded-2xl bg-brand-purple text-white p-8">
            <h2 className="font-heading text-2xl font-bold mb-3">Ready to secure a spot?</h2>
            <p className="font-body text-white/80 mb-6">
              Groups are capped, so places for {settings.term_label} genuinely run out. Tell us about the student and we'll call within 24 hours.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/enrol">
                <Button className="rounded-full bg-brand-gold hover:bg-brand-gold-hover text-white font-body font-semibold px-7">Enrol now</Button>
              </Link>
              <Link to="/pricing">
                <Button variant="outline" className="rounded-full border-white/40 text-white hover:bg-white/10 font-body font-semibold px-7">See pricing</Button>
              </Link>
            </div>
          </Reveal>

          <Reveal>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 dark:text-white mb-3">
              Free analysis of the texts we teach
            </h2>
            <p className="text-base text-slate-600 dark:text-slate-300 font-body mb-5">
              Every set text we teach has an in-house study guide. Read the themes, essay approach and the first
              few pages free.
            </p>
            <div className="flex flex-wrap gap-2" data-testid="service-guide-links">
              {TEXT_GUIDES.map((g) => (
                <Link key={g.slug} to={`/guides/${g.slug}`}
                  className="rounded-full border border-slate-200 dark:border-slate-700 px-4 py-2 text-sm font-body text-brand-purple dark:text-brand-purple-light hover:border-brand-purple transition-colors"
                  data-testid={`service-guide-link-${g.slug}`}>
                  {g.title}
                </Link>
              ))}
            </div>
          </Reveal>

          <Reveal>
            <h2 className="font-heading text-xl font-bold text-slate-900 dark:text-white mb-4">Explore our other classes</h2>
            <div className="flex flex-wrap gap-2">
              {SERVICES.filter((s) => s.slug !== service.slug).map((s) => (
                <Link key={s.slug} to={`/${s.slug}`}
                  className="rounded-full border border-slate-200 dark:border-slate-700 px-4 py-2 text-sm font-body text-brand-purple dark:text-brand-purple-light hover:border-brand-purple transition-colors"
                  data-testid={`service-link-${s.slug}`}>
                  {s.title}
                </Link>
              ))}
              <Link to="/blog" className="rounded-full border border-slate-200 dark:border-slate-700 px-4 py-2 text-sm font-body text-brand-purple dark:text-brand-purple-light hover:border-brand-purple transition-colors">
                Study tips & guides
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
